import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, ArrowRight, ArrowLeft, Database, Plane, ClipboardList, Zap, Smartphone, Plug } from "lucide-react";

const STEPS = [
  {
    number: 1,
    icon: Database,
    title: "Live Snapshot — How Data Starts",
    color: "text-orange-400",
    accent: "bg-orange-500/10 border-orange-500/20",
    dot: "bg-orange-500",
    route: null,
    cta: null,
    content: [
      {
        heading: "Seeded at a point in time",
        body: "When you click Reset Demo Data (or the system starts), the backend runs a seed function. It creates 15 flights, 225 task records, and 4 incidents in MongoDB — all with timestamps computed relative to right now.",
      },
      {
        heading: "Why it always looks live",
        body: "Each flight has an arrival_offset — for example, −28 means it landed 28 minutes ago. All 15 task planned times are calculated from that arrival moment. So every time you seed, the board looks like a fresh operational snapshot of today.",
      },
      {
        heading: "The scenarios",
        body: "Flights are pre-assigned a scenario: just_arrived, normal, at_risk, delayed, near_departure, arriving, departed. The scenario determines which tasks are complete, in progress, or blocked at seed time.",
      },
    ],
  },
  {
    number: 2,
    icon: Plane,
    title: "Flight Records — What Each Card Represents",
    color: "text-sky-400",
    accent: "bg-sky-500/10 border-sky-500/20",
    dot: "bg-sky-500",
    route: "/turnaround/board",
    cta: "See Live Board →",
    content: [
      {
        heading: "One MongoDB document per flight",
        body: "Each flight card on the Live Board maps to a ground_flights document. It stores: flight number, route, aircraft registration, station, stand, scheduled and actual arrival/departure times, inbound delay in minutes, and vendor assignments.",
      },
      {
        heading: "Computed summary fields",
        body: "Three fields are recalculated every time a task is updated: tasks_completed (count of done tasks), delay_risk_score (0–100), and current_bottleneck (the first delayed or blocked task description). These power the card UI without needing to re-fetch all 15 tasks.",
      },
      {
        heading: "Risk status label",
        body: "risk_status is derived from delay_risk_score: 0–25 = on_time (green), 26–55 = at_risk (amber), 56–100 = delayed (red). This drives the badge colour on every card.",
      },
    ],
  },
  {
    number: 3,
    icon: ClipboardList,
    title: "Task Timeline — The 15-Step Turnaround",
    color: "text-teal-400",
    accent: "bg-teal-500/10 border-teal-500/20",
    dot: "bg-teal-500",
    route: "/turnaround/board",
    cta: "Open a Flight to See Timeline →",
    content: [
      {
        heading: "15 tasks stored in ground_tasks",
        body: "Every flight has exactly 15 task records in a separate collection: Chocks On → Doors Open → Passengers Off → Fueling → Baggage Unload → Cabin Cleaning → Catering Load → Tech Check → Baggage Load → Catering Complete → Cleaning Complete → Crew Onboard → Boarding Complete → Doors Closed → Pushback.",
      },
      {
        heading: "The status field is the source of truth",
        body: "Each task has one status: not_started, in_progress, completed, delayed, blocked, or escalated. Planned time = arrival time + a fixed offset per task type. Actual time is recorded when a task is completed.",
      },
      {
        heading: "Critical path tasks",
        body: "9 of 15 tasks are flagged is_critical: true. These are Fueling, Baggage Unload, Tech Check, Baggage Load, Crew Onboard, Boarding, Doors Closed, and Pushback. A delay on any critical task triggers a larger risk score penalty.",
      },
    ],
  },
  {
    number: 4,
    icon: Zap,
    title: "Risk Score Engine — How 0–100 is Calculated",
    color: "text-amber-400",
    accent: "bg-amber-500/10 border-amber-500/20",
    dot: "bg-amber-500",
    route: "/turnaround/exceptions",
    cta: "See At-Risk Flights →",
    content: [
      {
        heading: "Scoring runs on every task update",
        body: "The moment any task status changes, the backend fetches all 15 tasks for that flight and recomputes the score from scratch. The updated score, label, and bottleneck are written back to the flight document instantly.",
      },
      {
        heading: "How points are added",
        body: "Blocked critical task: +25 pts. Delayed critical task: +20 pts. Delayed non-critical: +10 pts. Overdue in-progress task: up to +15 pts based on how many minutes over. Each incomplete critical task with less than 15 minutes to departure: +18 pts each.",
      },
      {
        heading: "The departure pressure multiplier",
        body: "The score accelerates as the departure window closes. A fueling delay with 45 minutes to go scores much lower than the same delay with 10 minutes to go — because there is no time left to recover. This is what gives the 30–90 minute early warning value.",
      },
    ],
  },
  {
    number: 5,
    icon: Smartphone,
    title: "Ramp App — The Manual Input Layer",
    color: "text-green-400",
    accent: "bg-green-500/10 border-green-500/20",
    dot: "bg-green-500",
    route: "/ramp",
    cta: "Open Ramp App →",
    content: [
      {
        heading: "How staff record updates",
        body: "Ground staff open the Ramp App on their phone or tablet. They select their role and station. The app shows only the tasks assigned to their role at their station, sorted by urgency: Overdue first, then Due Soon, then Upcoming.",
      },
      {
        heading: "Tapping a task",
        body: "Staff tap a task card to open the Update Task screen. They tap Start Task when they begin, Mark Complete when done, or Flag Delay if something is wrong. They can add a delay reason, a comment, and attach a photo from the device camera.",
      },
      {
        heading: "Incident reporting",
        body: "The Report tab lets any staff member file an incident: aircraft damage, safety hazard, equipment failure, staff shortage, vendor non-performance. Each report is timestamped, tagged with station and severity, and appears immediately in the Exceptions alert feed.",
      },
    ],
  },
  {
    number: 6,
    icon: Plug,
    title: "Production Path — Real Integrations",
    color: "text-purple-400",
    accent: "bg-purple-500/10 border-purple-500/20",
    dot: "bg-purple-500",
    route: "/turnaround/stations",
    cta: "See Station Dashboard →",
    content: [
      {
        heading: "Current state: manual input via Ramp App",
        body: "Right now, staff updates through the Ramp App are the live data source. This alone gives you a full audit trail, real-time risk scoring, and management visibility — with zero hardware required.",
      },
      {
        heading: "Next layer: automatic data feeds",
        body: "In a production deployment, the task engine can be fed by: AODB (Airport Operational Database) for arrival/departure actuals, fuel truck telemetry APIs for fueling status, check-in system APIs for boarding count, and baggage reconciliation systems for load status.",
      },
      {
        heading: "Phase 2: AI Delay Prediction",
        body: "The next module will use Claude AI to analyse the full turnaround state — inbound delay, task completion rate, vendor history, weather, stand congestion — and generate a Departure Confidence Score 30 to 90 minutes before pushback, with a plain-language reason and recommended action.",
      },
    ],
  },
];

export default function HowItWorksModal({ onClose }) {
  const [step, setStep] = useState(0);
  const navigate = useNavigate();
  const current = STEPS[step];
  const Icon = current.icon;

  function jumpTo(route) {
    onClose();
    if (route) navigate(route);
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(4,10,22,0.88)", backdropFilter: "blur(6px)" }}
    >
      <div className="w-full max-w-2xl bg-[#0c1929] border border-slate-700/40 rounded-2xl overflow-hidden shadow-2xl">

        {/* ── Top bar ──────────────────────────────────────────────────── */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700/30">
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-orange-400 font-black uppercase tracking-widest">How It Works</span>
            <span className="text-[10px] text-slate-600 font-mono">· Step {step + 1} of {STEPS.length}</span>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-white transition p-1 rounded hover:bg-white/5" data-testid="hiw-close">
            <X size={16} />
          </button>
        </div>

        {/* ── Progress dots ────────────────────────────────────────────── */}
        <div className="flex items-center gap-2 px-6 pt-4">
          {STEPS.map((s, i) => (
            <button
              key={i}
              onClick={() => setStep(i)}
              className={`transition-all rounded-full ${i === step ? `w-6 h-2 ${current.dot}` : "w-2 h-2 bg-slate-700 hover:bg-slate-500"}`}
              data-testid={`hiw-dot-${i}`}
            />
          ))}
        </div>

        {/* ── Step content ─────────────────────────────────────────────── */}
        <div className="px-6 py-5 min-h-[340px] flex flex-col">
          {/* Icon + title */}
          <div className="flex items-start gap-4 mb-5">
            <div className={`flex-shrink-0 w-12 h-12 rounded-xl border flex items-center justify-center ${current.accent}`}>
              <Icon size={22} className={current.color} />
            </div>
            <div>
              <div className={`text-[10px] font-black uppercase tracking-widest mb-1 ${current.color}`}>
                Step {current.number} / {STEPS.length}
              </div>
              <h2 className="text-lg font-black text-white leading-tight">{current.title}</h2>
            </div>
          </div>

          {/* Content blocks */}
          <div className="space-y-4 flex-1">
            {current.content.map((block, i) => (
              <div key={i} className={`rounded-xl border p-4 ${current.accent}`}>
                <div className={`text-[10px] font-black uppercase tracking-widest mb-1.5 ${current.color}`}>
                  {block.heading}
                </div>
                <p className="text-sm text-slate-300 leading-relaxed">{block.body}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ── Footer nav ───────────────────────────────────────────────── */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-700/30 bg-[#090f1e]">
          <button
            onClick={() => setStep(s => Math.max(0, s - 1))}
            disabled={step === 0}
            className="flex items-center gap-2 text-sm text-slate-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition px-3 py-2 rounded-lg hover:bg-white/5"
            data-testid="hiw-prev"
          >
            <ArrowLeft size={14} /> Previous
          </button>

          <div className="flex items-center gap-2">
            {current.route && (
              <button
                onClick={() => jumpTo(current.route)}
                className={`flex items-center gap-1.5 text-[11px] font-bold px-3 py-2 rounded-lg border transition ${current.accent} ${current.color} hover:opacity-80`}
                data-testid="hiw-jump"
              >
                {current.cta}
              </button>
            )}

            {step < STEPS.length - 1 ? (
              <button
                onClick={() => setStep(s => s + 1)}
                className="flex items-center gap-2 text-sm font-bold text-white bg-orange-500 hover:bg-orange-400 transition px-4 py-2 rounded-lg"
                data-testid="hiw-next"
              >
                Next <ArrowRight size={14} />
              </button>
            ) : (
              <button
                onClick={onClose}
                className="flex items-center gap-2 text-sm font-bold text-white bg-green-600 hover:bg-green-500 transition px-4 py-2 rounded-lg"
                data-testid="hiw-done"
              >
                Got it <ArrowRight size={14} />
              </button>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}
